import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { logError } from "@/lib/errorLogger";

/**
 * Hook to get or create a mentorship thread.
 * Used as a fallback when clicking "View Conversation" if thread_id is missing.
 */
export function useGetOrCreateMentorshipThread() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mentorshipRequestId: string): Promise<string> => {
      if (!user?.id) {
        const error = new Error("Not authenticated");
        await logError({
          error: error.message,
          component: "useGetOrCreateMentorshipThread",
          action: "auth_check",
          metadata: { mentorshipRequestId },
        });
        throw error;
      }

      console.log("[useGetOrCreateMentorshipThread] Calling RPC for request:", mentorshipRequestId);

      const { data, error } = await supabase
        .rpc("get_or_create_mentorship_thread", {
          p_mentorship_request_id: mentorshipRequestId,
        });

      if (error) {
        console.error("[useGetOrCreateMentorshipThread] RPC error:", error);
        
        // Log error for admin debugging
        await logError({
          error: `MENTORSHIP_THREAD_OPEN_FAILED: ${error.message}`,
          component: "useGetOrCreateMentorshipThread",
          action: "get_or_create_mentorship_thread",
          metadata: {
            mentorshipRequestId,
            userId: user.id,
            errorCode: error.code,
            errorDetails: error.details,
            errorHint: error.hint,
          },
        });
        throw error;
      }

      if (!data) {
        const noDataError = new Error("No thread ID returned from RPC");
        console.error("[useGetOrCreateMentorshipThread] No data returned");
        
        await logError({
          error: `MENTORSHIP_THREAD_OPEN_FAILED: ${noDataError.message}`,
          component: "useGetOrCreateMentorshipThread",
          action: "get_or_create_mentorship_thread",
          metadata: {
            mentorshipRequestId,
            userId: user.id,
            response: "null",
          },
        });
        throw noDataError;
      }

      console.log("[useGetOrCreateMentorshipThread] Got thread ID:", data);
      return data as string;
    },
    onSuccess: (threadId) => {
      console.log("[useGetOrCreateMentorshipThread] Success, invalidating queries. Thread:", threadId);
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-sent"] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-requests-received"] });
    },
    onError: (error: Error) => {
      console.error("[useGetOrCreateMentorshipThread] Mutation error:", error);
      toast.error(`Could not open conversation (code: MENTORSHIP_THREAD_OPEN_FAILED). Please try again.`);
    },
  });
}
